import config from "../config";
import { createApi } from "@reduxjs/toolkit/query/react";
import { prepareHeaders } from "./utils";
import { FDItemType, FDModalQuestionGroup } from "../../../api/src/types";
import { fdAnswerUpsertHasDocumentSideEffects } from "../../../api/src/users/answers/upsert-rules";
import { createSerializedFetchBaseQuery } from "./serializedFetchBaseQuery";
import {
  buildSerializedAnswerMutationHeaders,
  buildUpsertAnswerRequestBody,
  createCleanSlateTracker,
  patchQueryDataIfPresent,
  runOptimisticAnswerMutation,
} from "./answerMutationHelpers";
import {
  areQuestionsMarkedOptimistic,
  clearOptimisticQuestionMarkers,
  clearQuestionAnswerTreeInCache,
  markQuestionsOptimistic,
  mergeQuestionGroupsWithOptimisticMarkers,
} from "./answerTreeCache";
import {
  financialDisclosureResourcePaths,
  financialDisclosureKeys,
  patchDocumentClearApprovalsAndPDFs,
  type FinancialDisclosureTarget,
  requireFinancialDisclosureTarget,
} from "./financialDisclosureMutationHelpers";
import {
  financialDisclosuresApi,
  getFDStatusTag,
} from "./financials/financialDisclosures";

type FDModalAnswer =
  FDModalQuestionGroup["questions"][number]["answers"][number];

// todo: use hono response type from backend instead
interface FDAnswerUpsertMutationResponse {
  kind: "no-op" | "create" | "update" | "delete";
  answer?: FDModalAnswer;
  deletedChildQuestionIds: string[];
}

const fdAnswersCleanSlateTracker = createCleanSlateTracker({
  createKey: (params: {
    userId: string;
    documentId: string;
    fdItemType: FDItemType;
    fdItemId: string;
    questionId: string;
  }): string => {
    return `fd-answers-clean-slate:${params.userId}:${params.documentId}:${params.fdItemType}:${params.fdItemId}:${params.questionId}`;
  },
});

export type FDQuestionGroupsArgs = {
  userId: string;
  docId: string;
  fdItemType: FDItemType;
} & FinancialDisclosureTarget;

export const fdAnswersApi = createApi({
  reducerPath: "fdAnswersApi",
  baseQuery: createSerializedFetchBaseQuery({
    baseUrl: config.api,
    prepareHeaders,
  }),
  tagTypes: ["FDQuestionGroup"],
  endpoints: (builder) => ({
    getFDQuestionGroups: builder.query<
      FDModalQuestionGroup[],
      FDQuestionGroupsArgs
    >({
      serializeQueryArgs: ({ queryArgs }) => {
        const target = requireFinancialDisclosureTarget({
          itemId: queryArgs.itemId,
          draftKey: queryArgs.draftKey,
        });

        return financialDisclosureKeys.questionGroups({
          userId: queryArgs.userId,
          docId: queryArgs.docId,
          fdItemType: queryArgs.fdItemType,
          ...target,
        });
      },
      query: ({ userId, docId, fdItemType, itemId }) => ({
        url: `/users/${userId}/documents/${docId}/fd-questions`,
        method: "GET",
        params: itemId ? { fdItemType, fdItemId: itemId } : { fdItemType },
      }),
      merge: (currentCacheData, responseData) => {
        return mergeQuestionGroupsWithOptimisticMarkers({
          currentGroups: currentCacheData,
          incomingGroups: responseData,
        });
      },
      providesTags: (_result, _error, args) => [
        {
          type: "FDQuestionGroup" as const,
          id: financialDisclosureKeys.questionGroups({
            userId: args.userId,
            docId: args.docId,
            fdItemType: args.fdItemType,
            ...requireFinancialDisclosureTarget({
              itemId: args.itemId,
              draftKey: args.draftKey,
            }),
          }),
        },
      ],
    }),
    upsertFDAnswer: builder.mutation<
      FDAnswerUpsertMutationResponse,
      {
        questionId: string;
        userId: string;
        documentId: string;
        rootQuestionId: string;
        sectionId: string;
        fdItemType: FDItemType;
        fdItemId: string;
        rtkEditId: string;
        draftKey?: string;
        serializeDebounceMs?: number;
        valueTyped?: string | null;
        questionOptionIds?: string[];
      }
    >({
      query: ({
        questionId,
        userId,
        documentId,
        fdItemType,
        fdItemId,
        draftKey,
        serializeDebounceMs,
        valueTyped,
        questionOptionIds,
      }) => ({
        url: `/users/${userId}/documents/${documentId}/fd-answers`,
        method: "POST",
        params: { questionId, fdItemType, fdItemId },
        body: buildUpsertAnswerRequestBody({
          valueTyped,
          questionOptionIds,
          forceCleanSlate: fdAnswersCleanSlateTracker.has({
            userId,
            documentId,
            fdItemType,
            fdItemId,
            questionId,
          }),
        }),
        headers: buildSerializedAnswerMutationHeaders({
          serializeBy: financialDisclosureKeys.mutation({
            documentId,
            resourcePath: financialDisclosureResourcePaths[fdItemType],
            itemId: fdItemId,
            ...(draftKey ? { draftKey } : {}),
          }),
          pruneBy: `${questionId}-FD-POST`,
          serializeDebounceMs,
        }),
      }),
      async onQueryStarted(
        {
          questionId,
          userId,
          documentId,
          fdItemType,
          fdItemId,
          draftKey,
          valueTyped,
          questionOptionIds,
          rtkEditId,
        },
        api,
      ) {
        const args = {
          userId,
          docId: documentId,
          fdItemType,
          itemId: fdItemId,
          ...(draftKey ? { draftKey } : {}),
        };

        // Mirror the server-side document-artifact clear so the cached doc
        // reflects the new (unapproved) state immediately. On no-op success
        // or error we refetch rather than inverse-patch (see onSuccess/onError).
        const optimisticDocPatch = patchDocumentClearApprovalsAndPDFs({
          dispatch: api.dispatch,
          getState: () => api.getState(),
          documentId,
        });

        await runOptimisticAnswerMutation({
          dispatch: api.dispatch,
          getState: () => api.getState(),
          queryFulfilled: api.queryFulfilled,
          cache: {
            args,
            selectQueryState: (state, args) =>
              fdAnswersApi.endpoints.getFDQuestionGroups.select(args)(state),
            updateQueryData: (args, updater) =>
              fdAnswersApi.util.updateQueryData(
                "getFDQuestionGroups",
                args,
                updater,
              ),
          },
          optimisticTreeUpdate: {
            questionId,
            valueTyped,
            questionOptionIds,
            createOptimisticAnswer: ({
              questionId,
              normalizedValueTyped,
              normalizedOptionIds,
              now,
            }) => {
              const answerId = `optimistic-fd-answer-${questionId}`;

              return {
                id: answerId,
                questionId,
                userId,
                documentId,
                fdItemType,
                fdItemId,
                valueTyped: normalizedValueTyped,
                deletedAt: null,
                createdAt: now.toISOString(),
                updatedAt: now.toISOString(),
                createdById: userId,
                updatedById: userId,
                answerQuestionOptions: normalizedOptionIds.map((optionId) => ({
                  questionOptionId: optionId,
                  answerId,
                  isDefault: false,
                  createdAt: now.toISOString(),
                  updatedAt: now.toISOString(),
                })),
              };
            },
            createAnswerQuestionOption: ({ answerId, optionId, now }) => ({
              questionOptionId: optionId,
              answerId,
              isDefault: false,
              createdAt: now.toISOString(),
              updatedAt: now.toISOString(),
            }),
          },
          cleanSlateKey: {
            userId,
            documentId,
            fdItemType,
            fdItemId,
            questionId,
          },
          cleanSlateTracker: fdAnswersCleanSlateTracker,
          shouldUndoOnError: ({ state, cacheArgs, affectedQuestionIds }) => {
            const queryState =
              fdAnswersApi.endpoints.getFDQuestionGroups.select(cacheArgs)(
                state,
              );

            return (
              queryState.data != null &&
              areQuestionsMarkedOptimistic({
                draft: queryState.data,
                questionIds: affectedQuestionIds,
                rtkEditId,
              })
            );
          },
          onOptimisticUpdate: ({
            draft,
            affectedQuestionIds,
            optimisticNow,
          }) => {
            markQuestionsOptimistic({
              draft,
              questionIds: affectedQuestionIds,
              marker: {
                rtkEditId,
                updatedAt: optimisticNow.toISOString(),
              },
            });
          },
          onSuccess: ({
            dispatch,
            state,
            cacheArgs,
            result,
            affectedQuestionIds,
          }) => {
            patchQueryDataIfPresent({
              dispatch,
              state,
              args: cacheArgs,
              selectQueryState: (state, args) =>
                fdAnswersApi.endpoints.getFDQuestionGroups.select(args)(state),
              updateQueryData: (args, updater) =>
                fdAnswersApi.util.updateQueryData(
                  "getFDQuestionGroups",
                  args,
                  updater,
                ),
              updater: (draft) => {
                clearOptimisticQuestionMarkers({
                  draft,
                  questionIds: affectedQuestionIds,
                  rtkEditId,
                });

                const question = draft
                  .flatMap((group) => group.questions)
                  .find((candidate) => candidate.id === questionId);
                if (!question) {
                  return;
                }

                if (result.kind === "delete") {
                  question.answers = [];
                  return;
                }

                if (result.kind === "no-op") {
                  return;
                }

                const answer = result.answer;
                if (!answer) {
                  return;
                }

                const currentAnswer = question.answers[0];
                if (!currentAnswer) {
                  return;
                }

                currentAnswer.id = answer.id;
                currentAnswer.updatedAt = answer.updatedAt;
              },
            });

            dispatch(
              financialDisclosuresApi.util.invalidateTags([
                getFDStatusTag(documentId),
              ]),
            );
            // Server reporting "no document side effects" means it didn't
            // run `clearDocumentApprovalsAndPDFs`, so a previously
            // applied client-side patch needs to be reconciled via refetch
            // (modeled identically to the upsert mutation's no-op success).
            optimisticDocPatch(
              fdAnswerUpsertHasDocumentSideEffects(result)
                ? "success"
                : "server-noop",
            );
          },
          onError: ({ dispatch }) => {
            optimisticDocPatch("error");
            dispatch(
              fdAnswersApi.util.invalidateTags([
                {
                  type: "FDQuestionGroup",
                  id: financialDisclosureKeys.questionGroups({
                    userId,
                    docId: documentId,
                    fdItemType,
                    itemId: fdItemId,
                    ...(draftKey ? { draftKey } : {}),
                  }),
                },
              ]),
            );
          },
        });
      },
    }),
  }),
});

export const { useGetFDQuestionGroupsQuery, useUpsertFDAnswerMutation } =
  fdAnswersApi;

/**
 * Mirrors the backend's `clearHiddenRootDescendantFDAnswers` against the
 * RTK Query cache. When an FD item's `isJoint` flips, root questions
 * keyed to the now-hidden visibility ("CP" when joint goes false, "SP"
 * when joint goes true) and their entire subtrees should appear empty
 * in the modal — otherwise the previously-entered answers would
 * resurface if the user toggles `isJoint` back before a refetch.
 */
export function clearHiddenRootDescendantAnswersInCache(params: {
  dispatch: (action: unknown) => unknown;
  userId: string;
  docId: string;
  fdItemType: FDItemType;
  itemId?: string | null | undefined;
  draftKey?: string | null | undefined;
  isJoint: boolean;
}): void {
  const { dispatch, userId, docId, fdItemType, itemId, draftKey, isJoint } =
    params;

  const target = requireFinancialDisclosureTarget({ itemId, draftKey });
  const args: FDQuestionGroupsArgs = {
    userId,
    docId,
    fdItemType,
    ...target,
  };

  const hiddenVisibility = isJoint ? "SP" : "CP";

  dispatch(
    fdAnswersApi.util.updateQueryData(
      "getFDQuestionGroups",
      args,
      (draft: FDModalQuestionGroup[]) => {
        const hiddenRootIds = draft
          .flatMap((group) => group.questions)
          .filter(
            (q) =>
              q.parentQuestionId === null &&
              q.rootQuestionVisibility === hiddenVisibility,
          )
          .map((q) => q.id);

        // Reuses the same parent+descendants clear that fires when a
        // parent answer changes (see answerTreeCache.ts), one call per
        // newly-hidden root.
        for (const rootId of hiddenRootIds) {
          clearQuestionAnswerTreeInCache({
            draft,
            parentQuestionId: rootId,
          });
        }
      },
    ),
  );
}
